/* Talent Search — Stap 1 in het Recruit-pillar.
 *
 * Multi-source kandidaat-sourcing (Apollo · LinkedIn X-ray · Apify · Perplexity)
 * met doelpool-keuze, pool-badges, bulk-verrijking en shortlist-building.
 *
 * Dit bestand is de orchestrator: state + API-handlers + toolbar.
 * UI-componenten leven in components/recruit/search/.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { api } from "@/lib/api";
import { PageHeader } from "../Dashboard";
import { toast } from "sonner";
import {
    Search as SearchIcon, Plus, Bookmark, Loader2, LayoutGrid, List, Radar,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LookalikeModal } from "@/components/recruit/LookalikeFinder";
import SkillsTrendPanel from "@/components/recruit/SkillsTrendPanel";
import SearchForm from "@/components/recruit/search/SearchForm";
import SavedSearchesSection from "@/components/recruit/search/SavedSearchesSection";
import SmartTalentHuntsRow from "@/components/recruit/search/SmartTalentHunts";
import CandidateCard, { EmptyPool } from "@/components/recruit/search/CandidateCard";
import CandidatesTable from "@/components/recruit/search/CandidatesTable";
import { CandidateFormModal, DeleteConfirmModal } from "@/components/recruit/search/CandidateModals";
import { sourceLabelOf } from "@/components/recruit/search/constants";

export default function TalentSearch() {
    const { t } = useTranslation();
    const nav = useNavigate();
    const [urlParams] = useSearchParams();
    const [candidates, setCandidates] = useState([]);
    const [savedSearches, setSavedSearches] = useState([]);
    const [activeSearchId, setActiveSearchId] = useState(urlParams.get("search") || "");
    const [loading, setLoading] = useState(true);
    const [searching, setSearching] = useState(false);
    const [jd, setJd] = useState("");
    const [filters, setFilters] = useState({
        skill: "", location: "", country: "", size: "",
        sector: "", seniority: [], onlyNew: false, pool: "all",
    });
    const [picked, setPicked] = useState(new Set());
    const [lastSearchInfo, setLastSearchInfo] = useState(null); // { new, reused, titles, sources_used }
    const [showLookalike, setShowLookalike] = useState(false);
    // Multi-source: geselecteerde zoekbronnen (init = alle geconfigureerde)
    const [searchSources, setSearchSources] = useState([]);
    // Talentpools van de workspace → key→naam map voor badges + filter
    const [pools, setPools] = useState([]);
    const poolMap = useMemo(() => Object.fromEntries(pools.map((p) => [p.key, p.name])), [pools]);
    // Doelpool waarin NIEUWE zoekresultaten worden opgeslagen
    const [targetPool, setTargetPool] = useState("external");
    // Bulk-verrijking van geselecteerde kandidaten — met live voortgang
    const [enrichProgress, setEnrichProgress] = useState(null);   // { done, total, failed } | null
    // Bulk-verplaatsing naar een andere talentpool
    const [bulkMoving, setBulkMoving] = useState(false);
    // View mode + CRUD modal state
    const [viewMode, setViewMode] = useState(() => localStorage.getItem("candidates_view") || "cards");
    const [editing, setEditing] = useState(null);   // candidate object or "new"
    const [confirmDelete, setConfirmDelete] = useState(null);   // candidate object
    useEffect(() => { localStorage.setItem("candidates_view", viewMode); }, [viewMode]);

    // Track candidates sourced via the latest search so the "onlyNew"
    // chip has something to filter to.
    const [freshIds, setFreshIds] = useState(new Set());

    const loadCandidates = () => {
        api.get("/candidates").then((r) => { setCandidates(r.data); setLoading(false); });
    };

    const loadSearches = () => {
        api.get("/talent/saved-searches").then((r) => setSavedSearches(r.data || [])).catch(() => setSavedSearches([]));
    };

    useEffect(() => {
        loadCandidates();
        loadSearches();
        api.get("/talent-pool/pools").then((r) => setPools(r.data || [])).catch(() => setPools([]));
    }, []);

    // If url contains ?search=xxx, pre-open the drawer + scope candidate list
    useEffect(() => {
        const sid = urlParams.get("search");
        if (sid) setActiveSearchId(sid);
    }, [urlParams]);

    const runSearch = async () => {
        if (jd.trim().length < 3) {
            return toast.error(i18n.t("search.min_chars_error"));
        }
        if (searchSources.length === 0) {
            return toast.error(i18n.t("search.pick_sources"));
        }
        setSearching(true);
        try {
            const r = await api.post("/talent/search", {
                jd: jd.trim(),
                titles: [],
                skills: filters.skill ? [filters.skill] : [],
                location: filters.country || filters.location || null,
                seniority: filters.seniority,
                company_size: filters.size || null,
                sector: filters.sector || null,
                per_page: 15,
                sources: searchSources,
                target_pool: targetPool,
            });
            const found = r.data.candidates || [];
            const newIds = new Set((r.data.candidates || []).slice(0, r.data.new_count || 0).map((c) => c.id));
            setFreshIds(newIds);
            // Merge results: newly-added at the top, existing preserved below (avoid duplicates)
            setCandidates((prev) => {
                const map = new Map(prev.map((c) => [c.id, c]));
                for (const c of found) map.set(c.id, c);
                return Array.from(map.values());
            });
            setLastSearchInfo({
                new: r.data.new_count || 0,
                reused: r.data.reused_count || 0,
                titles: r.data.titles_searched || [],
                source: r.data.source || "apollo",
                sources_used: r.data.sources_used || [],
                fallback_reason: r.data.fallback_reason || null,
                saved_search_id: r.data.saved_search_id || null,
            });
            if (r.data.saved_search_id) setActiveSearchId(r.data.saved_search_id);
            loadSearches();
            if (found.length === 0) {
                toast.warning(i18n.t("search.no_matches"));
            } else {
                const sourceLabel = (r.data.sources_used || [])
                    .filter((s) => s.found > 0)
                    .map((s) => sourceLabelOf(s.id))
                    .join(" + ") || sourceLabelOf(r.data.source);
                toast.success(i18n.t("search.results_toast", { new_count: r.data.new_count || 0, reused: r.data.reused_count || 0, source: sourceLabel }));
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || i18n.t("search.search_failed"));
        } finally { setSearching(false); }
    };

    const filtered = useMemo(() => {
        return candidates.filter((c) => {
            if (activeSearchId && !(c.saved_search_ids || []).includes(activeSearchId)) return false;
            if (filters.pool !== "all" && (c.talent_pool || "external") !== filters.pool) return false;
            if (filters.onlyNew && !freshIds.has(c.id)) return false;
            if (filters.skill && !(c.skills || []).join(" ").toLowerCase().includes(filters.skill.toLowerCase())) return false;
            if (filters.location && !(c.location || "").toLowerCase().includes(filters.location.toLowerCase())) return false;
            if (filters.seniority.length > 0 && !filters.seniority.includes(c.seniority)) return false;
            return true;
        });
    }, [candidates, filters, freshIds, activeSearchId]);

    const activeSearch = savedSearches.find((s) => s.id === activeSearchId);

    const deleteSearch = async (id, label) => {
        if (!confirm(i18n.t("search.delete_search_confirm", { label }))) return;
        try {
            await api.delete(`/talent/saved-searches/${id}`);
            toast.success(i18n.t("common.deleted"));
            if (activeSearchId === id) setActiveSearchId("");
            loadSearches();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("search.delete_failed")); }
    };

    const toggle = (id) => {
        const next = new Set(picked);
        next.has(id) ? next.delete(id) : next.add(id);
        setPicked(next);
    };

    const makeShortlist = async () => {
        if (picked.size === 0) return toast.error(i18n.t("search.pick_min"));
        const name = prompt(i18n.t("search.shortlist_prompt"), jd.slice(0, 30) || i18n.t("search.shortlist_default"));
        if (!name) return;
        try {
            await api.post("/shortlists", { name, role_title: jd.slice(0, 60), description: jd, candidate_ids: Array.from(picked) });
            toast.success(i18n.t("search.shortlist_created", { name, count: picked.size }));
            nav(`/app/recruit/shortlists`);
        } catch (e) { toast.error(i18n.t("common.failed")); }
    };

    // Bulk deep-enrich van alle geselecteerde kandidaten (met LinkedIn-URL).
    // Client-side wachtrij met 3 parallelle workers → live voortgangsbalk.
    const bulkEnrich = async () => {
        const withLi = candidates.filter((c) => picked.has(c.id) && c.linkedin_url);
        if (withLi.length === 0) return toast.error(i18n.t("search.bulk_enrich_no_linkedin"));
        if (!window.confirm(i18n.t("search.bulk_enrich_confirm", { count: withLi.length }))) return;
        const queue = withLi.map((c) => c.id);
        const total = queue.length;
        let done = 0, failed = 0;
        setEnrichProgress({ done: 0, total, failed: 0 });
        const worker = async () => {
            while (queue.length > 0) {
                const id = queue.shift();
                try { await api.post(`/profile-resolver/deep-enrich/${id}`); }
                catch { failed += 1; }
                done += 1;
                setEnrichProgress({ done, total, failed });
            }
        };
        await Promise.all(Array.from({ length: Math.min(3, total) }, worker));
        setEnrichProgress(null);
        setPicked(new Set());   // cost-safety: voorkom per ongeluk een 2e betaalde run
        toast.success(i18n.t("search.bulk_enrich_done", { enriched: total - failed, failed }));
        loadCandidates();
    };

    // Bulk-verplaatsing van geselecteerde kandidaten naar een andere pool.
    const bulkMovePool = async (poolKey) => {
        if (!poolKey || picked.size === 0) return;
        setBulkMoving(true);
        try {
            const r = await api.post("/talent-pool/bulk-move", {
                candidate_ids: Array.from(picked), pool: poolKey,
            });
            toast.success(i18n.t("search.moved_ok", { count: r.data.moved, pool: r.data.pool_name }));
            setPicked(new Set());
            loadCandidates();
        } catch (e) {
            toast.error(e?.response?.data?.detail || i18n.t("common.failed"));
        } finally { setBulkMoving(false); }
    };

    return (
        <PageHeader
            title={t("search.title")}
            subtitle={t("search.subtitle")}
        >
            <SmartTalentHuntsRow onDone={loadSearches}/>
            <SearchForm
                jd={jd} setJd={setJd}
                filters={filters} setFilters={setFilters}
                searchSources={searchSources} setSearchSources={setSearchSources}
                targetPool={targetPool} setTargetPool={setTargetPool} pools={pools}
                searching={searching} lastSearchInfo={lastSearchInfo}
                onRun={runSearch} onOpenLookalike={() => setShowLookalike(true)}
            />

            <SkillsTrendPanel onAddSkill={(s) => setJd((prev) => (prev?.trim() ? `${prev.trim()}, ${s}` : s))} />

            <LookalikeModal open={showLookalike} onClose={() => setShowLookalike(false)} />

            {/* Saved Searches list — first see which searches exist, then drill in */}
            {savedSearches.length > 0 && (
                <SavedSearchesSection
                    searches={savedSearches}
                    activeId={activeSearchId}
                    onPick={(id) => setActiveSearchId(id === activeSearchId ? "" : id)}
                    onDelete={deleteSearch}
                />
            )}

            {/* Toolbar — teller, pool-filter, nieuw, view-toggle, bulk-acties */}
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="text-sm text-[var(--jr-text-2)]">
                    <SearchIcon size={12} className="inline mr-1"/>
                    {activeSearch && (
                        <span className="text-white font-medium mr-2">
                            Search: <span className="text-[var(--jr-amber)]">{activeSearch.label}</span> ·
                        </span>
                    )}
                    {filtered.length} {t("search.in_list")} · {picked.size} {t("search.picked")}
                    {activeSearch && (
                        <button onClick={() => setActiveSearchId("")} data-testid="clear-search-filter"
                            className="ml-2 text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] hover:text-white underline">
                            {t("search.clear_filter")}
                        </button>
                    )}
                </div>
                {/* Pool filter — dynamisch: alle pools van de workspace */}
                <div className="flex items-center gap-1 text-xs flex-wrap" data-testid="pool-filter">
                    <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mr-1">Pool</span>
                    {[
                        { id: "all", label: `${i18n.t("common.all")} · ${candidates.length}` },
                        ...pools.map((p) => ({
                            id: p.key,
                            label: `${p.name} · ${candidates.filter((c) => (c.talent_pool || "external") === p.key).length}`,
                        })),
                    ].map((p) => (
                        <button key={p.id} onClick={() => setFilters({ ...filters, pool: p.id })} data-testid={`pool-${p.id}`}
                            className={`text-[11px] px-2.5 py-0.5 rounded-full border transition-colors ${filters.pool === p.id
                                ? "bg-[var(--jr-amber)] text-black border-[var(--jr-amber)]"
                                : "border-white/10 text-[var(--jr-text-2)] hover:bg-white/5"}`}>
                            {p.label}
                        </button>
                    ))}
                </div>
                <button onClick={() => setEditing("new")} data-testid="candidate-new"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm text-[var(--jr-text-2)] hover:bg-white/5 hover:text-white">
                    <Plus size={13}/> Nieuw
                </button>
                {/* View mode toggle */}
                <div className="inline-flex rounded-full border border-white/10 overflow-hidden" data-testid="view-mode-toggle">
                    <button onClick={() => setViewMode("cards")} data-testid="view-cards"
                        className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-mono uppercase tracking-[0.16em] transition-colors ${viewMode === "cards" ? "bg-[var(--jr-brand)] text-white" : "text-[var(--jr-text-2)] hover:bg-white/5"}`}>
                        <LayoutGrid size={11}/> Kaarten
                    </button>
                    <button onClick={() => setViewMode("list")} data-testid="view-list"
                        className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-mono uppercase tracking-[0.16em] transition-colors ${viewMode === "list" ? "bg-[var(--jr-brand)] text-white" : "text-[var(--jr-text-2)] hover:bg-white/5"}`}>
                        <List size={11}/> Lijst
                    </button>
                </div>
                {/* Bulk verplaatsen naar andere pool */}
                <select value="" onChange={(e) => bulkMovePool(e.target.value)}
                    disabled={picked.size === 0 || bulkMoving}
                    data-testid="bulk-move-pool"
                    title={i18n.t("search.move_to_pool_tip")}
                    className="px-3 py-2 rounded-full border border-white/10 bg-[var(--jr-surface)] text-sm text-[var(--jr-text-2)] outline-none hover:bg-white/5 disabled:opacity-50 cursor-pointer">
                    <option value="" disabled>{bulkMoving ? i18n.t("search.moving") : `${i18n.t("search.move_to_pool")} (${picked.size})`}</option>
                    {pools.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
                </select>
                {/* Bulk verrijken — alle geselecteerde kandidaten in één keer */}
                <button onClick={bulkEnrich} disabled={picked.size === 0 || enrichProgress !== null} data-testid="bulk-enrich"
                    title={i18n.t("search.bulk_enrich_tip")}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#a855f7]/50 text-[#a855f7] text-sm font-medium hover:bg-[#a855f7]/10 disabled:opacity-50">
                    {enrichProgress !== null ? <Loader2 size={14} className="animate-spin"/> : <Radar size={14}/>}
                    {i18n.t("search.bulk_enrich_btn")} ({picked.size})
                </button>
                <button onClick={makeShortlist} disabled={picked.size === 0} data-testid="make-shortlist"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--jr-amber)] text-black text-sm font-medium hover:bg-[var(--jr-amber)]/80 disabled:opacity-50">
                    <Bookmark size={14}/> {t("search.make_shortlist")} ({picked.size})
                </button>
            </div>

            {/* Live voortgang van bulk-verrijking */}
            {enrichProgress && (
                <div className="mb-3 rounded-lg border border-[#a855f7]/30 bg-[#a855f7]/[0.06] px-4 py-2.5" data-testid="enrich-progress">
                    <div className="flex items-center gap-2 text-xs">
                        <Radar size={12} className="text-[#a855f7] animate-pulse" />
                        <span className="text-white font-medium">
                            {i18n.t("search.enrich_progress", { done: enrichProgress.done, total: enrichProgress.total })}
                        </span>
                        {enrichProgress.failed > 0 && (
                            <span className="text-[var(--jr-error)]">· {enrichProgress.failed} {i18n.t("search.enrich_failed_count")}</span>
                        )}
                        <span className="ml-auto font-mono text-[#a855f7]" data-testid="enrich-progress-pct">
                            {Math.round((enrichProgress.done / enrichProgress.total) * 100)}%
                        </span>
                    </div>
                    <div className="mt-2 h-1.5 rounded-full bg-white/8 overflow-hidden">
                        <div className="h-full rounded-full bg-[#a855f7] transition-all duration-300"
                            data-testid="enrich-progress-bar"
                            style={{ width: `${(enrichProgress.done / enrichProgress.total) * 100}%` }} />
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-[var(--jr-text-2)]"><Loader2 size={13} className="animate-spin inline mr-1"/>{i18n.t("common.loading")}</div>
            ) : filtered.length === 0 ? (
                <EmptyPool onRun={runSearch} disabled={searching || jd.trim().length < 3} />
            ) : viewMode === "list" ? (
                <CandidatesTable candidates={filtered} picked={picked} onToggle={toggle}
                    poolMap={poolMap}
                    onOpen={(c) => nav(`/app/recruit/candidate/${c.id}`)}
                    onEdit={(c) => setEditing(c)}
                    onDelete={(c) => setConfirmDelete(c)}/>
            ) : (
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="candidate-grid">
                    {filtered.map((c) => (
                        <CandidateCard key={c.id} c={c} isPicked={picked.has(c.id)}
                            isFresh={freshIds.has(c.id)} onToggle={() => toggle(c.id)}
                            poolName={poolMap[c.talent_pool || "external"]}
                            onEnriched={loadCandidates}
                            onOpen={() => nav(`/app/recruit/candidate/${c.id}`)}
                            onEdit={() => setEditing(c)}
                            onDelete={() => setConfirmDelete(c)}/>
                    ))}
                </div>
            )}
            {editing && (
                <CandidateFormModal candidate={editing === "new" ? null : editing}
                    pools={pools}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); loadCandidates(); }}/>
            )}
            {confirmDelete && (
                <DeleteConfirmModal candidate={confirmDelete}
                    onCancel={() => setConfirmDelete(null)}
                    onConfirmed={() => { setConfirmDelete(null); loadCandidates(); }}/>
            )}
        </PageHeader>
    );
}
